export default function Troubleshooting() {
  return (
    <section id="troubleshooting">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Troubleshooting & FAQ</h2>

      <div className="space-y-6">
        {/* FAQ */}
        <div className="space-y-4">
          {[
            {
              q: 'Where are my Minecraft Bedrock worlds stored?',
              a: 'Since Minecraft switched to the GDK version, worlds are typically found in: %appdata%\\Minecraft Bedrock\\Users\\<user ID>\\games\\com.mojang\\minecraftWorlds. Each subfolder is a world. You don\'t usually need to find this folder yourself — the start screen automatically detects and lists worlds from all installed Minecraft editions (Bedrock, Preview, and Education Edition), grouped by source. You can also add custom folders in Settings if you store worlds in other locations.'
            },
            {
              q: 'Can I edit worlds from consoles (Xbox, PlayStation, Switch)?',
              a: 'Not directly. Console worlds need to be exported to a Windows device first. Some consoles support Realm transfers or USB export. Once you have the world files on your PC, Bedrock Toolchest can edit them, and you can transfer them back.'
            },
            {
              q: 'Why can\'t I see any players in the Player Editor?',
              a: 'Players only appear if they have saved data in the world. If nobody has ever joined the world (e.g. a freshly created world), there may be no player data to show. The local player data is created the first time you load the world in Minecraft.'
            },
            {
              q: 'My changes aren\'t showing up in Minecraft.',
              a: 'Make sure you clicked Save Changes in Bedrock Toolchest before opening the world in Minecraft. Also make sure Minecraft fully closed and reloaded the world — some changes won\'t appear if you just leave and re-enter the world without closing it completely.'
            },
            {
              q: 'Is it safe to edit a world while it\'s open in Minecraft?',
              a: 'No. Always close the world in Minecraft before editing it. Editing while the world is open can cause data loss or corruption, since both programs would be writing to the same database files.'
            },
            {
              q: 'Can I undo changes after saving?',
              a: 'Yes — use the Backups tab to restore a previous version of the world. A backup is automatically created every time you save, so you can always go back to the state before your last edit.'
            },
            {
              q: 'Does this work with Java Edition worlds?',
              a: 'No. Bedrock Toolchest only supports Minecraft Bedrock Edition worlds. Java Edition uses a different world format.'
            },
            {
              q: 'Why does the Entities tab take a while to load?',
              a: 'Large worlds can have thousands or even tens of thousands of entities. The program scans the entire world database to find them, which can take a few seconds on very large worlds. This is why entities are loaded on demand rather than automatically.'
            },
            {
              q: 'Can I use this to give myself items?',
              a: 'Yes. Open the Player Editor, go to the Inventory tab, and click on any inventory slot to add or change items. You can set the item type, stack size, enchantments (with no level limit), custom names, and repair durability.'
            },
          ].map(({ q, a }, i) => (
            <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{q}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{a}</p>
            </div>
          ))}
        </div>

        {/* Common Issues */}
        <div>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Common Issues</h3>
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">The program won't open / crashes on startup</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Make sure you're running Windows 10 or later (64-bit). If Windows Defender or your
                antivirus is blocking the program, you may need to add an exception for the .exe file.
                Try right-clicking the file and selecting "Run as administrator" if it won't start.
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">"World is locked" or "database in use" error</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                This means Minecraft (or another tool) still has the world open. Close Minecraft
                completely and try again. If the error persists, check Task Manager for any lingering
                Minecraft processes.
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Saving takes a long time</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Save time depends on the size of the world and the number of changes. Creating the
                backup (which happens before every save) is usually the slowest part for large worlds.
                The save progress dialog shows what's happening.
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Player names show as IDs instead of gamertags</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Minecraft Bedrock doesn't always store player names in the world data — it depends on
                how the player joined. If a player's name isn't stored, the program shows their internal
                ID instead. There's an optional add-on (PlayerName Tagger) linked from the Player Editor
                that writes player names to the world data for better display.
              </p>
            </div>
          </div>
        </div>

        {/* Get Help */}
        <div className="rounded-xl border border-primary-200 dark:border-primary-800/50 bg-primary-50 dark:bg-primary-900/20 p-5">
          <h3 className="font-semibold text-primary-800 dark:text-primary-300 mb-2">Need more help?</h3>
          <p className="text-sm text-primary-700 dark:text-primary-400">
            Join our{' '}
            <a href="https://discord.mygen.co.uk" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary-600 dark:hover:text-primary-300">
              Discord server
            </a>
            {' '}to ask questions, report bugs, or suggest features. The community and developer are
            active and happy to help.
          </p>
        </div>
      </div>
    </section>
  );
}
